import BaseScene from '@Scenes/BaseScene';
import EventEmitter from '@Utils/Events/EventEmitter';
import ToDataUrl from '@Utils/Image/ToDataURL';
import CreateScoreImage from '@Utils/Image/CreateScoreImage';

/*--------------- Config ------------------------*/
import Config from '@Config';
import { LeaderboardIDs } from '@Config/leaderboards';
import * as Scenes from '@Config/scenes';
import KeyCodes from '@Config/keycodes';
import AnalyticsEvents from '@Config/analytics_events';

/*--------------- Core ------------------------*/
import Game from '@Game';
import Network from '@Networks/Localhost';
import LanguageManager from '@Languages/LanguageManager';


class CompleteScene extends BaseScene
{
    /**
     * Leaderboard Button
     */
    private leaderboardButton: Phaser.GameObjects.Container;

    /**
     * Score button
     */
    private scoreButton: Phaser.GameObjects.Container;

    /**
     * Share button
     */
    private shareButton: Phaser.GameObjects.Container;
    private shareButtonBackground: Phaser.GameObjects.Image;

    /**
     * Add to home button
     */
    private addToHomeButton: Phaser.GameObjects.Container;

    /**
     * Tap to continue hitarea
     */
    private tapToContinueHitArea: Phaser.Geom.Rectangle;

    /**
     * Contructor
     */
    constructor()
    {
        super(Scenes.COMPLETE)
    }

    /**
     * Create score scene
     */
    public async create()
    {
        this.enableTapToContinue();
        this.createBaseControls();
        this.showScore();
        this.bindEvents();
        this.checkAddToHomeButtonVisibility();

        EventEmitter.emit(Scenes.COMPLETE, 'scoreShown');
    }


    /**
     * Creates Buttons
     */
    private createBaseControls()
    {
        this.createContinueText();
        this.createLeaderboardButton();
        this.createScoreButton();
        this.createChallengeButton();

        if( Network.getSupportedAPIs().indexOf('shareAsync') !== -1)
        {
            this.createShareButton();
        }

        this.createAddToHomeButton();
    }

    /**
     * Creates continue texta
     */
    private createContinueText(): void
    {
        const gameSize = this.getGameSize();

        const fontStyle = { fontFamily: 'Permanent Marker' };

        const tryAgainButton = this.add.image((gameSize.width / 2), gameSize.height - 60, 'try_again_button');
        tryAgainButton.alpha = 0;

        const restartIcon = this.add.image(345, 497, 'restart_icon');
        restartIcon.setDisplaySize(50,50);
        restartIcon.alpha = 0;

        const endText = LanguageManager.getLanguage(`end_action-${Config.DEVICE}`);
        const continueText = this.add.text(gameSize.centerX + 15, gameSize.height - 65, endText, fontStyle);
        continueText.setOrigin(0.5);
        continueText.setFontSize(30);
        continueText.setStroke('#000000', 3);
        continueText.setColor('#FFFFFF');

        continueText.alpha = 0;

        this.add.tween({ targets: [continueText, restartIcon, tryAgainButton], alpha: 1, duration: 500, delay: 500});
    }

    /**
     * Creates leaderboard icon
     */
    private createLeaderboardButton(): void
    {
        const gameSize = this.getGameSize();

        const leaderboardButton = this.add.container(gameSize.width - 75, gameSize.height - 75);

        const buttonBackground = this.add.image(0, 0, 'btn_grey');
        buttonBackground.setDisplaySize(90, 90);
        buttonBackground.setInteractive();
        buttonBackground.on('pointerdown', () =>
        {
            buttonBackground.setDisplaySize(100, 100);
        });

        buttonBackground.on('pointerup', () =>
        {
            Network.logEvent(AnalyticsEvents.END_SCREEN_SHOW_LEADERBOARDS);
            buttonBackground.setDisplaySize(90, 90);

            if( Network.getSupportedAPIs().indexOf('getLeaderboardAsync') !== -1 )
            {
                this.showLeaderboards();
            }
            else
            {
                this.scene.launch(Scenes.FEATURE_NOT_AVAILABLE);
            }
        });

        const leaderboardIcon = this.add.image(0, 0, 'leaderboard_icon');
        leaderboardIcon.setDisplaySize(45, 45);

        leaderboardButton.add(buttonBackground)
        leaderboardButton.add(leaderboardIcon);

        this.leaderboardButton = leaderboardButton;
    }

    /**
    * Creates leaderboard icon
    */
    private createScoreButton(): void
    {
        const gameSize = this.getGameSize();

        const scoreButton = this.add.container(gameSize.width - 75, gameSize.height - 75);

        const buttonBackground = this.add.image(0, 0, 'btn_grey');
        buttonBackground.setDisplaySize(90, 90);
        buttonBackground.setInteractive();
        buttonBackground.on('pointerdown', () =>
        {
            buttonBackground.setDisplaySize(100, 100);
        });

        buttonBackground.on('pointerup', () =>
        {
            Network.logEvent(AnalyticsEvents.END_SCREEN_SHOW_SCORE);
            buttonBackground.setDisplaySize(90, 90);
            this.showScore();
        });

        const scoreIcon = this.add.image(0, 0, 'score_icon');
        scoreIcon.setDisplaySize(45, 45);
        scoreButton.add(buttonBackground)
        scoreButton.add(scoreIcon);

        this.scoreButton = scoreButton;
    }

    /**
     * Creates Challenge icon
     */
    private createChallengeButton(): void
    {
        const gameSize = this.getGameSize();

        const challengeButton = this.add.container(75, gameSize.height - 75);

        const buttonBackground = this.add.image(0, 0, 'btn_grey');
        buttonBackground.setDisplaySize(90, 90);
        buttonBackground.setInteractive();
        buttonBackground.on('pointerdown', () =>
        {
            buttonBackground.setDisplaySize(100, 100);
        });

        buttonBackground.on('pointerup', () =>
        {
            Network.logEvent(AnalyticsEvents.END_SCREEN_SWITCH_CONTEXT);
            buttonBackground.setDisplaySize(90, 90);

            if (Network.getSupportedAPIs().indexOf('context.chooseAsync') !== -1)
            {
                this.switchContext();
            }
            else
            {
                this.scene.launch(Scenes.FEATURE_NOT_AVAILABLE);
            }
        });

        const challengeIcon = this.add.image(0, 0, 'challenge_icon');
        challengeIcon.setDisplaySize(45, 45);
        challengeButton.add(buttonBackground)
        challengeButton.add(challengeIcon);
    }


    /**
     * Creates Share Button
     */
    private createShareButton(): void
    {
        const gameSize = this.getGameSize();

        const shareButton = this.add.container(170, gameSize.height - 75);

        const buttonBackground = this.add.image(0, 0, 'btn_grey');
        buttonBackground.setDisplaySize(90, 90);
        buttonBackground.setInteractive();
        buttonBackground.on('pointerdown', () =>
        {
            buttonBackground.setDisplaySize(100, 100);
        });

        buttonBackground.on('pointerup', () =>
        {
            Network.logEvent(AnalyticsEvents.END_SCREEN_SWITCH_CONTEXT);
            buttonBackground.setDisplaySize(90, 90);
            this.shareScore();
        });

        const shareIcon = this.add.image(0, 0, 'share_score_icon');
        shareIcon.setDisplaySize(31, 25);
        shareButton.add(buttonBackground)
        shareButton.add(shareIcon);

        this.shareButton = shareButton;
        this.shareButtonBackground = buttonBackground;
    }

    /**
     * Shares score
     */
    private async shareScore()
    {
        this.shareButtonBackground.disableInteractive();
        this.shareButton.alpha = 0.2;

        const totalScore = Game.runScore.getTotalScore().toString();

        const shareSplashTexture = this.textures.get('splash');
        const shareSplashImage = shareSplashTexture.getSourceImage() as HTMLImageElement;

        const scoreSplashImage = CreateScoreImage(shareSplashImage, totalScore);

        try
        {
            await Network.shareAsync({
                intent: 'REQUEST',
                image: scoreSplashImage,
                text: `${Network.player.getName()} just scored ${totalScore} points!`,
            });
        }
        catch (error)
        {
            Network.logEvent(AnalyticsEvents.FAILED_TO_SHARE_SCORE, null, { error_code: error.code });
        }

        this.shareButtonBackground.setInteractive()
        this.shareButton.alpha = 1;
    }

    /**
     * Check add to home visibility
     */
    private async checkAddToHomeButtonVisibility()
    {
        try
        {
            const canCreateShortcut = await Network.canCreateShortcutAsync();

            if (canCreateShortcut)
            {
                this.addToHomeButton.setVisible(true);
                this.add.tween({ targets: this.addToHomeButton, duration: 500, alpha: 1 });
            }
        }
        catch (error)
        {
            Network.logEvent(AnalyticsEvents.FAILED_TO_ADD_TO_HOME_SCREEN, null, { error_code: error.code, non_interaction:'true' });
        }
    }

    /**
     * Creates Share Button
     */
    private createAddToHomeButton(): void
    {
        const gameSize = this.getGameSize();

        const addToHomeButton = this.add.container(gameSize.width - 170, gameSize.height - 75);

        const buttonBackground = this.add.image(0, 0, 'btn_grey');
        buttonBackground.setDisplaySize(90, 90);
        buttonBackground.setInteractive();
        buttonBackground.on('pointerdown', () =>
        {
            buttonBackground.setDisplaySize(100, 100);
        });

        buttonBackground.on('pointerup', () =>
        {
            Network.logEvent(AnalyticsEvents.END_SCREEN_ADD_TO_HOME_SCREEN);
            buttonBackground.setDisplaySize(90, 90);
            this.addToHome();
        });

        const addToHomeIcon = this.add.image(0, 0, 'add_to_home_icon');
        addToHomeIcon.setDisplaySize(45, 45);
        addToHomeButton.add(buttonBackground)
        addToHomeButton.add(addToHomeIcon);
        addToHomeButton.alpha = 0;
        addToHomeButton.setVisible(false);

        this.addToHomeButton = addToHomeButton
    }

    /**
     * Add to home screen
     */
    private async addToHome()
    {
        this.addToHomeButton.setVisible(false);

        try
        {
            await Network.createShortcutAsync();
        }
        catch (error)
        {
            Network.logEvent(AnalyticsEvents.FAILED_TO_ADD_TO_HOME_SCREEN, null, { error_code: error.code });

            this.addToHomeButton.setVisible(true);
        }
    }

    /**
     * Switches context, utlizing network context
     */
    private async switchContext()
    {
        try
        {
            await Network.context.chooseAsync();

            const contextualLeaderboard = Game.leaderboards[LeaderboardIDs.CONTEXTUAL];

            await contextualLeaderboard.initializeAsync();

            /**
             * Check if the context is new.
             * If it's new, tell the other players about the game.
             */
            const leaderboardEntriesCount = await contextualLeaderboard.getEntryCountAsync();

            if (leaderboardEntriesCount <= 0)
            {
                const shareSplashTexture = this.textures.get('splash');

                const shareSplashImage = shareSplashTexture.getSourceImage() as HTMLImageElement;

                await Network.updateAsync({
                    action: 'CUSTOM',
                    cta: "Play",
                    template: 'challenge',
                    image: ToDataUrl(shareSplashImage),
                    strategy: 'IMMEDIATE',
                    text: `${Network.player.getName()} is challenging you to beat their score! `
                });
            }

            if (this.scene.isActive(Scenes.LEADERBOARD))
            {
                const leaderboardScene = this.scene.get(Scenes.LEADERBOARDS);
                leaderboardScene.scene.restart();
            }

            // Restart players scene to read new scores / context
            if(this.scene.isActive(Scenes.PLAYERS))
            {
                const playersScene = this.scene.get(Scenes.PLAYERS);

                playersScene.scene.restart();
            }
        }
        catch (error)
        {
            Network.logEvent(AnalyticsEvents.FAILED_TO_SWITCH_CONTEXT, null, { error_code: error.code });
        }
    }

    /**
     * Enables tap to continue
     */
    private enableTapToContinue(): void
    {
        const gameSize = this.getGameSize();

        this.tapToContinueHitArea = new Phaser.Geom.Rectangle((gameSize.width / 2) - 200, gameSize.height - 150, 400, 150);

        this.input.on('pointerdown', this.onTap.bind(this));
        this.input.keyboard.on('keydown', this.onKeyDown.bind(this));

    }

    /**
     * Binds to events
     */
    private bindEvents(): void
    {
        this.enableShutdown();
    }

    /**
     * Shows the next screen
     */
    private onTap(pointer: Phaser.Input.Pointer): void
    {
        if (this.tapToContinueHitArea.contains(pointer.x, pointer.y))
        {
            this.playAgain();
        };
    }

    /**
     * Emits play again event
     */
    private playAgain(): void
    {
        EventEmitter.emit(Scenes.COMPLETE, 'playAgain');
    }

    /**
     * On key down
     * @param event
     */
    private onKeyDown(event: KeyboardEvent): void
    {
        if (KeyCodes.START === event.keyCode)
        {
            this.playAgain()
        }
    }

    /**
     * Event Tap to restart
     */
    private showScore()
    {
        this.scoreButton.visible = false;
        this.leaderboardButton.visible = true;

        this.scene.stop(Scenes.LEADERBOARDS);
        this.scene.launch(Scenes.END_SCORE);
    }

    /**
     * Shows the leaderboard screen
     */
    private showLeaderboards()
    {
        this.scoreButton.visible = true;
        this.leaderboardButton.visible = false;

        this.scene.stop(Scenes.END_SCORE);
        this.scene.launch(Scenes.LEADERBOARDS);
    }

    /**
     * Shuts down the scene
     */
    public shutdown()
    {
        Game.freerider.blur(0);

        this.scene.stop(Scenes.END_SCORE);
        this.scene.stop(Scenes.LEADERBOARDS);
    }

}

export default CompleteScene;